import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './App.css';
import './MemoManagement.css';
import logger from './utils/logger';

function formatMemoDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString('ko-KR', { dateStyle: 'medium', timeStyle: 'short' });
}

function MemoManagement() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editModal, setEditModal] = useState({ open: false, id: null, title: '', body: '' });
  const [formData, setFormData] = useState({ title: '', body: '' });
  const [submitting, setSubmitting] = useState(false);

  const fetchMemos = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/memos', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      logger.apiError('GET', '/api/memos', err);
      setError('메모 목록을 불러오지 못했습니다.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMemos();
  }, []);

  const openAddModal = () => {
    setFormData({ title: '', body: '' });
    setModalOpen(true);
  };

  const closeAddModal = () => {
    setModalOpen(false);
    setFormData({ title: '', body: '' });
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!formData.body.trim()) {
      alert('메모 내용을 입력해주세요.');
      return;
    }
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      await axios.post(
        '/api/memos',
        { title: formData.title.trim(), body: formData.body.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      closeAddModal();
      await fetchMemos();
    } catch (err) {
      logger.apiError('POST', '/api/memos', err);
      alert(err?.response?.data?.error || '메모 등록에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditModal = (item) => {
    setEditModal({
      open: true,
      id: item.id,
      title: item.title || '',
      body: item.body || ''
    });
  };

  const closeEditModal = () => {
    setEditModal({ open: false, id: null, title: '', body: '' });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editModal.body.trim()) {
      alert('메모 내용을 입력해주세요.');
      return;
    }
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      await axios.put(
        `/api/memos/${editModal.id}`,
        { title: editModal.title.trim(), body: editModal.body.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      closeEditModal();
      await fetchMemos();
    } catch (err) {
      logger.apiError('PUT', '/api/memos/:id', err);
      alert(err?.response?.data?.error || '메모 수정에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (item) => {
    const label = item.title?.trim() || `메모 #${item.id}`;
    if (!window.confirm(`「${label}」을(를) 삭제할까요?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/memos/${item.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchMemos();
    } catch (err) {
      logger.apiError('DELETE', '/api/memos/:id', err);
      alert(err?.response?.data?.error || '메모 삭제에 실패했습니다.');
    }
  };

  return (
    <div className="memo-management-container">
      <div className="section-card">
        <div className="section-header">
          <div>
            <h1 className="section-title">업무 메모</h1>
            <p className="section-subtitle">슈퍼·본사만 접근합니다. 짧은 메모를 남기고 수정·삭제할 수 있습니다.</p>
          </div>
          <button type="button" className="create-user-btn" onClick={openAddModal}>
            + 메모 작성
          </button>
        </div>

        {loading ? (
          <div className="loading">불러오는 중...</div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : items.length === 0 ? (
          <div className="no-data">등록된 메모가 없습니다.</div>
        ) : (
          <ul className="memo-list">
            {items.map((item) => (
              <li key={item.id} className="memo-card">
                <div className="memo-card-head">
                  <h2 className="memo-card-title">
                    {item.title?.trim() ? item.title : '(제목 없음)'}
                  </h2>
                  <div className="memo-card-meta">
                    <span>{formatMemoDate(item.updated_at || item.created_at)}</span>
                    {item.created_by_username && (
                      <span className="memo-card-author">{item.created_by_username}</span>
                    )}
                  </div>
                </div>
                <pre className="memo-card-body">{item.body}</pre>
                <div className="memo-card-actions">
                  <button type="button" className="edit-user-btn" onClick={() => openEditModal(item)}>
                    수정
                  </button>
                  <button type="button" className="delete-user-btn" onClick={() => handleDelete(item)}>
                    삭제
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {modalOpen && (
        <div className="modal-overlay">
          <div className="modal memo-modal">
            <div className="modal-header">
              <h3>메모 작성</h3>
              <button type="button" className="modal-close" onClick={closeAddModal}>
                ×
              </button>
            </div>
            <form onSubmit={handleAddSubmit} className="modal-content">
              <div className="form-group">
                <label htmlFor="memo-title-new">제목 (선택)</label>
                <input
                  id="memo-title-new"
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="제목"
                  maxLength={200}
                />
              </div>
              <div className="form-group">
                <label htmlFor="memo-body-new">내용 *</label>
                <textarea
                  id="memo-body-new"
                  rows={10}
                  value={formData.body}
                  onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                  placeholder="메모 내용을 입력하세요."
                  required
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="cancel-btn" onClick={closeAddModal} disabled={submitting}>
                  취소
                </button>
                <button type="submit" className="confirm-btn" disabled={submitting}>
                  {submitting ? '저장 중...' : '저장'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editModal.open && (
        <div className="modal-overlay">
          <div className="modal memo-modal">
            <div className="modal-header">
              <h3>메모 수정</h3>
              <button type="button" className="modal-close" onClick={closeEditModal}>
                ×
              </button>
            </div>
            <form onSubmit={handleEditSubmit} className="modal-content">
              <div className="form-group">
                <label htmlFor="memo-title-edit">제목 (선택)</label>
                <input
                  id="memo-title-edit"
                  type="text"
                  value={editModal.title}
                  onChange={(e) => setEditModal({ ...editModal, title: e.target.value })}
                  maxLength={200}
                />
              </div>
              <div className="form-group">
                <label htmlFor="memo-body-edit">내용 *</label>
                <textarea
                  id="memo-body-edit"
                  rows={10}
                  value={editModal.body}
                  onChange={(e) => setEditModal({ ...editModal, body: e.target.value })}
                  required
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="cancel-btn" onClick={closeEditModal} disabled={submitting}>
                  취소
                </button>
                <button type="submit" className="confirm-btn" disabled={submitting}>
                  {submitting ? '저장 중...' : '저장'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default MemoManagement;
